import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { authFetch } from '../services/authFetch';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '';
const CategoryContext = createContext(null);

export const useCategories = () => {
  const context = useContext(CategoryContext);
  if (!context) {
    throw new Error('useCategories must be used within a CategoryProvider');
  }
  return context;
};

export const useCategoriesOptional = () => useContext(CategoryContext);

export const CategoryProvider = ({ children }) => {
  const [allCategories, setAllCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetched, setFetched] = useState(false);

  // /api/categories requires a token. This provider sits above the router (App.js:770),
  // so without a gate it fetched on every boot — including on /login, where no token
  // exists — and the backend answered 401 on every single page load.
  //
  // Gating on isAuthenticated rather than on `token` because a stale token can survive a
  // reload: isAuthenticated is `!!user && appState === 'active'`, which is the same
  // condition the protected routes use. Every consumer of this context
  // (Phase73CategoryReviewQueue, InventoryDashboard, QuoteBuilder) is behind
  // ProtectedRoute, so nothing needs categories before that point.
  const { isAuthenticated } = useAuth();

  const fetchCategories = useCallback(async () => {
    if (fetched || !isAuthenticated) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${API_URL}/api/categories`);
      // Check the status before parsing. A 401/500 body is still valid JSON, so without
      // this the error body parsed cleanly, `raw` fell through to [], and `fetched` was
      // set true below — leaving categories PERMANENTLY empty for the rest of the session
      // with no error surfaced. A failed fetch must stay un-fetched so it can retry.
      if (!res.ok) {
        throw new Error(`GET /api/categories failed (${res.status})`);
      }
      const json = await res.json();
      const raw = json.categories || json.data?.categories || json.data || [];

      // Flatten nested hierarchy (L1 with children arrays) into a flat list
      const flat = [];
      const flatten = (items, parentId) => {
        if (!Array.isArray(items)) return;
        items.forEach(item => {
          const { children, subcategories, ...cat } = item;
          if (!cat.parent_id && parentId) cat.parent_id = parentId;
          flat.push(cat);
          if (children && children.length > 0) flatten(children, cat.id);
          if (subcategories && subcategories.length > 0) flatten(subcategories, cat.id);
        });
      };

      // Detect structure: nested (has children) vs flat (has level)
      if (raw.length > 0 && (raw[0].children || raw[0].subcategories)) {
        flatten(raw, null);
      } else {
        flat.push(...raw);
      }

      setAllCategories(flat);
      setFetched(true);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [fetched, isAuthenticated]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  // Signing out clears the cache, so the next user does not inherit the previous one's
  // categories and a fresh fetch runs on their first authenticated render. Also drops
  // isLoading, which starts true and would otherwise sit true forever on the login page.
  useEffect(() => {
    if (isAuthenticated) return;
    setFetched(false);
    setAllCategories([]);
    setError(null);
    setIsLoading(false);
  }, [isAuthenticated]);

  // Derived collections
  const departments = useMemo(
    () => allCategories.filter(c => c.level === 1 && c.is_active !== false),
    [allCategories]
  );

  const mainCategories = useMemo(
    () => allCategories.filter(c => c.level === 2 && c.is_active !== false),
    [allCategories]
  );

  const subcategories = useMemo(
    () => allCategories.filter(c => c.level === 3 && c.is_active !== false),
    [allCategories]
  );

  // Lookup maps
  const byId = useMemo(() => {
    const map = {};
    allCategories.forEach(c => { map[c.id] = c; });
    return map;
  }, [allCategories]);

  const bySlug = useMemo(() => {
    const map = {};
    allCategories.forEach(c => { if (c.slug) map[c.slug] = c; });
    return map;
  }, [allCategories]);

  const getCategoryById = useCallback((id) => {
    if (!id) return null;
    return byId[id] || null;
  }, [byId]);

  const getCategoryBySlug = useCallback((slug) => {
    if (!slug) return null;
    return bySlug[slug] || null;
  }, [bySlug]);

  /**
   * Get display name for a product's category.
   * Resolves category_id → category name, with fallback to parent L1 name.
   * Never returns null, blank, or "Miscellaneous".
   */
  const getCategoryLabel = useCallback((product) => {
    if (!product) return 'Uncategorized';

    // Primary: resolved category_id
    const catId = product.category_id || product.categoryId;
    if (catId && byId[catId]) {
      return byId[catId].display_name || byId[catId].name;
    }

    // Fallback: try subcategory_id
    const subId = product.subcategory_id || product.subcategoryId;
    if (subId && byId[subId]) {
      return byId[subId].display_name || byId[subId].name;
    }

    // Fallback: category_name if backend joined it
    if (product.category_name && product.category_name !== 'Uncategorized' && product.category_name !== 'Miscellaneous') {
      return product.category_name;
    }

    // Fallback: master_category from inventory API
    if (product.master_category && product.master_category !== 'Misc.' && product.master_category !== 'Miscellaneous') {
      return product.master_category;
    }

    // Last resort
    return 'General';
  }, [byId]);

  /**
   * Get L2 categories filtered by a department (L1) id.
   * If no departmentId, returns all L2 categories.
   */
  const getCategoriesForDepartment = useCallback((departmentId) => {
    if (!departmentId) return mainCategories;
    return mainCategories.filter(c => c.parent_id === departmentId);
  }, [mainCategories]);

  const value = useMemo(() => ({
    categories: allCategories,
    departments,
    mainCategories,
    subcategories,
    getCategoryById,
    getCategoryBySlug,
    getCategoryLabel,
    getCategoriesForDepartment,
    isLoading,
    error,
    refetch: () => { setFetched(false); }
  }), [allCategories, departments, mainCategories, subcategories, getCategoryById, getCategoryBySlug, getCategoryLabel, getCategoriesForDepartment, isLoading, error]);

  return (
    <CategoryContext.Provider value={value}>
      {children}
    </CategoryContext.Provider>
  );
};
